// Ranking graczy (patch-35): kto z rodzeństwa zdobył najwięcej punktów. Punkty = gwiazdki za wymowę (te same, które dziecko widzi na ekranie głównym).
//  • „Ten tydzień” — gwiazdki z sesji (`history`) od poniedziałku do niedzieli bieżącego tygodnia,
//  • „Wszystkie czasy” — suma gwiazdek konta (`Progress.stars`).
// Czysta logika (bez DOM i sieci), wspólna dla serwera (liczy punkty z zapisanego postępu każdego profilu) i aplikacji (układa miejsca).
import type { Progress } from '../types.ts';
import { addDays } from './dates.ts';
import type { Gender } from './gender.ts';
import type { Look } from './look.ts';
import { weekStart } from './mood.ts';

export type RankScope = 'week' | 'all';

/** Jeden gracz w odpowiedzi serwera: wygląd i dwie sumy punktów. */
export interface RankPlayer extends Look {
  id: string;
  name: string;
  gender: Gender;
  /** Punkty z bieżącego tygodnia. */
  week: number;
  /** Punkty łącznie. */
  total: number;
}

/**
 * Gracz z miejscem w wybranej tabeli. Remis dzieli miejsce (1, 1, 3). Kto ma 0 punktów, **nie ma miejsca** (`place` = 0): nie trafia na podium i nie dostaje numeru,
 * nawet gdy wszyscy wyżej już się policzyli (patch-49: wcześniej gracze z zerem dostawali „3.” i „#3” jednocześnie).
 */
export interface Placed extends RankPlayer {
  points: number;
  /** 1, 2, 3… (remis: to samo miejsce); 0 = bez punktów, bez miejsca. */
  place: number;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Czy to poprawna data kalendarzowa RRRR-MM-DD (np. nie 2026-13-45). */
export function isIsoDate(v: unknown): v is string {
  if (typeof v !== 'string' || !ISO_DATE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

const whole = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

/** Punkty z tygodnia, w którym jest `today` (poniedziałek–niedziela): suma gwiazdek z zapisanych sesji z tych dni. */
export function weekPoints(p: Progress, today: string): number {
  const from = weekStart(today);
  const to = addDays(from, 6);
  let sum = 0;
  for (const h of p.history) if (h.date >= from && h.date <= to) sum += whole(h.stars);
  return sum;
}

/** Punkty łącznie (gwiazdki konta). */
export const totalPoints = (p: Progress): number => whole(p.stars);

/** Punkty gracza w wybranej tabeli. */
export const pointsOf = (p: Pick<RankPlayer, 'week' | 'total'>, scope: RankScope): number => (scope === 'week' ? p.week : p.total);

/** Układa graczy od najlepszego: punkty malejąco, przy remisie alfabetycznie po imieniu; remis dzieli miejsce, a 0 punktów nie daje miejsca (na końcu listy, alfabetycznie). */
export function rankPlayers(players: readonly RankPlayer[], scope: RankScope): Placed[] {
  const sorted = players
    .map((p) => ({ ...p, points: pointsOf(p, scope) }))
    .sort((a, b) => b.points - a.points || a.name.localeCompare(b.name, 'pl') || a.id.localeCompare(b.id));
  let place = 0;
  return sorted.map((p, i) => {
    if (p.points <= 0) return { ...p, place: 0 };
    if (i === 0 || p.points !== sorted[i - 1]!.points) place = i + 1;
    return { ...p, place };
  });
}
